import axios from "axios";
import { setInterceptors } from "@/api/common/interceptors";

// axios.defaults.baseURL = 'http://127.0.0.1:8000' // 서버주소
// axios.defaults.headers.post['Content-Type'] = 'application/json;charset=utf-8'
// axios.defaults.headers.post['Access-Control-Allow-Origin'] = 'http://127.0.0.1:8000'
// axios.defaults.withCredentials = true

function createInstance() {
  const instance = axios.create({
    // baseURL: "http://0.0.0.0:8000",
    baseURL: process.env.VUE_APP_BASE_URL,
  });
  return setInterceptors(instance);
}

const instance = createInstance();

function registerUser(userData) {
  return instance.post("/routes/auth/register", userData);
}

function loginUser(userData) {
  const formData = new URLSearchParams();
  formData.append('username', userData.username);
  formData.append('password', userData.password);
  
  return instance.post("/routes/auth/login/access-token", formData.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
}

function getUser() {
  return instance.get("/routes/auth/me");
}

function getFilteredUsers(conditions) {
  return instance.get("/routes/admin/users", { params: conditions });
}

function getFilteredFiles(conditions) {
  return instance.get("/routes/admin/files", { params: conditions });
}

function getFilteredWorkflows(conditions) {
  return instance.get("/routes/admin/workflows", { params: conditions });
}

function getFilteredTasks(conditions) {
  return instance.get("/routes/admin/tasks", { params: conditions });
}

function getFilteredPlugins(conditions) {
  return instance.get("/routes/admin/plugins", { params: conditions });
}

function getUsersCount() {
  return instance.get("/routes/admin/users_count");
}

function getFilesCount() {
  return instance.get("/routes/admin/files_count");
}

function getWorkflowsCount() {
  return instance.get("/routes/admin/workflows_count");
}

function getTasksCount() {
  return instance.get("/routes/admin/tasks_count");
}

function getPluginsCount() {
  return instance.get("/routes/admin/plugins_count");
}

function getSystemResources() {
  return instance.get("/routes/admin/system/stats");
}

function getTaskResources() {
  return instance.get("/routes/task/resources");
}

function exportData(data) {
  return instance.post("/routes/workflow/compile", data);
}

function getResult(WorkflowResult) {
  return instance.post("/routes/workflow/result", WorkflowResult, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function getResults(WorkflowResult) {
  return instance.post("/routes/workflow/results", WorkflowResult);
}

function runVisualization(WorkflowUpdate) {
  return instance.post("/routes/workflow/visualization", WorkflowUpdate);
}

function getVisualizationResult(WorkflowResult) {
  return instance.post("/routes/workflow/visualization/result", WorkflowResult);
}

function uploadForm(formData, onUploadProgress) {
  // FormData의 value 확인
  // for (let value of formData.values()) {
  //   console.log(value)
  // }
  return instance.post("/routes/files/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress,
    timeout: 3600000, // 1 hour — matches nginx proxy_read_timeout
  });
}

function getFiles() {
  return instance.get("/routes/files/me");
}

function getSharedFiles() {
  return instance.get("/routes/files/shared");
}

function findSharedFile(fileInfo) {
  return instance.post("/routes/files/shared/find", fileInfo);
}

function findFile(fileInfo) {
  return instance.post("/routes/files/find", fileInfo);
}

function findFolder(folder) {
  return instance.post("/routes/files/folder", folder);
}

function deleteFile(file) {
  return instance.post("/routes/files/delete", file);
}

function convertFile(file) {
  return instance.post("/routes/files/convert", file);
}

function checkOptions() {
  return instance.get("/routes/files/setup/check");
}

function getOptions(file) {
  return instance.get(`/routes/files/setup/${file}`);
}

function checkConvert(file) {
  return instance.get(`/routes/files/check/${file}`);
}

function getColumns(fileInfo) {
  return instance.post("/routes/files/columns", fileInfo);
}

function getClusters(fileInfo) {
  return instance.post("/routes/files/clusters", fileInfo);
}

function setupAlgorithm(options) {
  return instance.post("/routes/files/setup", options);
}

function getFileData(filename, source) {
  const params = source ? { source } : {};
  return instance.get(`/routes/files/data/${filename}`, { params });
}

function getWorkflows() {
  return instance.get("/routes/workflow/me");
}

function findWorkflow(workflowInfo) {
  return instance.post("/routes/workflow/find", workflowInfo);
}

function saveWorkflow(workflow) {
  return instance.post("/routes/workflow/save", workflow);
}

function deleteWorkflow(workflow) {
  return instance.post("/routes/workflow/delete", workflow);
}

function saveWorkflowNodeFile(workflowNodeFileInfo) {
  return instance.post("/routes/workflow/node/save", workflowNodeFileInfo);
}

function deleteWorkflowNodeFile(workflowNodeFileInfo) {
  return instance.post("/routes/workflow/node/delete", workflowNodeFileInfo);
}

function readWorkflowNodeFile(workflowNodeFileInfo) {
  return instance.post("/routes/workflow/node/read", workflowNodeFileInfo);
}

function taskMonitoring(taskId) {
  return instance.get(`/routes/task/info/${taskId}`);
}

function userTaskMonitoring() {
  return instance.get("/routes/task/monitoring");
}

function revokeTask(taskId) {
  return instance.delete(`/routes/task/revoke/${taskId}`);
}

function deleteTask(taskId) {
  return instance.delete(`/routes/task/delete/${taskId}`);
}

function getTaskLogs(taskId) {
  return instance.get(`/routes/task/logs/${taskId}`);
}

function exportTaskLogsJSON(taskId) {
  return instance.get(`/routes/task/logs/${taskId}/export/json`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function exportTaskLogTXT(taskId, filename) {
  return instance.get(`/routes/task/logs/${taskId}/export/txt/${filename}`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function downloadExecutionManifest(taskId) {
  return instance.get(`/routes/task/${taskId}/execution-manifest`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function getHtml(filename) {
  return instance.get(`/routes/files/html/${filename}`);
}

function getDownloadResult(filename) {
  return instance.get(`/routes/files/result/${filename}`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function getTutorialFileDownload(filename) {
  return instance.get(`/routes/files/tutorials/${filename}`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function validationPlugin(plugin, rules, drawflow) {
  const data = {
    plugin,
    rules,
    drawflow,
  };

  return instance.post("/routes/plugin/validation", data);
}

function uploadPluginMetadata(pluginCreate) {
  return instance.post("/routes/plugin/upload", pluginCreate);
}

function syncPluginData(pluginName) {
  return instance.post(`/routes/plugin/update/${pluginName}`);
}

function uploadPluginScripts(formData) {
  return instance.post("/routes/plugin/upload_scripts", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

function uploadPluginPackage(formData) {
  return instance.post("/routes/plugin/upload_package", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
}

function uploadTextDependencies(formData) {
  return instance.post("/routes/plugin/upload_text_dependencies", formData);
}

function getPlugins() {
  return instance.get("/routes/plugin/list");
}

function associatePlugin(plugin_id) {
  return instance.post("/routes/plugin/associate", { plugin_id : plugin_id });
}

function dissociatePlugin(plugin_id) {
  return instance.post("/routes/plugin/dissociate", { plugin_id : plugin_id });
}

function getPluginTemplate(plugin_id) {
  return instance.get(`/routes/plugin/template/${plugin_id}`);
}

function getPluginFile(file_info) {
  return instance.get(`/routes/plugin/file/${file_info.plugin_name}/${file_info.file_name}`, {
    responseType: "blob", // 서버로부터 받은 데이터를 blob 형태로 처리
  });
}

function getPluginPackageList(plugin_name) {
  return instance.get(`/routes/plugin/package/${plugin_name}`);
}

function getPluginReferenceFolders(plugin_name) {
  return instance.get(`/routes/plugin/reference_folders/${plugin_name}`);
}

function getDataTableFile(vgt_info) {
  return instance.post("/routes/datatable/load_data", vgt_info);
}

function getPluginInfo(name) {
  return instance.get(`/routes/plugin/info/${name}`);
}

function buildPlugin(plugin_name) {
  return instance.post(`/routes/plugin/build/${plugin_name}`);
}

function buildPluginDocker(plugin_name, use_gpu = false) {
  return instance.post(`/routes/plugin/build_docker/${plugin_name}`, { use_gpu });
}

function checkPluginImage(plugin_name) {
  return instance.get(`/routes/plugin/check_image/${plugin_name}`);
}

// 새로운 플러그인 빌드 관련 API 함수들
function getBuildStatus(task_id) {
  return instance.get(`/routes/plugin/build/status/${task_id}`);
}

function getBuildTasks(plugin_name = null) {
  const params = plugin_name ? { plugin_name } : {};
  return instance.get('/routes/plugin/build/tasks', { params });
}

function cancelBuildTask(task_id) {
  return instance.post(`/routes/plugin/build/cancel/${task_id}`);
}

function getBuildLogs(plugin_name) {
  return instance.get(`/routes/plugin/build/logs/${plugin_name}`);
}

function getPluginVersions(plugin_name) {
  return instance.get(`/routes/plugin/versions/${plugin_name}`);
}

function updatePluginVersion(plugin_name, version) {
  const formData = new FormData();
  formData.append('version', version);
  return instance.post(`/routes/plugin/versions/${plugin_name}/update`, formData);
}

// DAG Visualization APIs
function getDAGStructure(taskId) {
  return instance.get(`/routes/task/${taskId}/dag-structure`);
}

function getRuleStatuses(taskId, taskStatus = null) {
  const params = taskStatus ? { actual_status: taskStatus } : {};
  return instance.get(`/routes/task/${taskId}/rule-status`, { params });
}

function getRuleLogs(taskId, ruleName) {
  return instance.get(`/routes/task/${taskId}/rule-logs/${ruleName}`);
}

function getEnhancedProgress(taskId) {
  return instance.get(`/routes/task/${taskId}/enhanced-progress`);
}

// Cache Management APIs
function getDAGCacheStats() {
  return instance.get(`/routes/task/cache/stats`);
}

function clearDAGCaches() {
  return instance.delete(`/routes/task/cache/clear`);
}

function getTaskStatusSimple(taskId) {
  return fetch(`${process.env.VUE_APP_BASE_URL}/routes/task/status/${taskId}`)
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
}

function createTaskEventSource(taskId, callbacks = {}) {
  const TERMINAL_STATES = ['SUCCESS', 'FAILURE', 'REVOKED'];
  const MAX_CONSECUTIVE_FAILURES = 3;
  const RECONNECT_DELAY_MS = 5000;

  let closed = false;
  let consecutiveFailures = 0;
  let reconnectTimer = null;
  let currentEventSource = null;

  function cleanup() {
    closed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (currentEventSource) {
      currentEventSource.close();
      currentEventSource = null;
    }
  }

  function scheduleReconnect() {
    if (closed) return;
    reconnectTimer = setTimeout(() => {
      if (!closed) connect();
    }, RECONNECT_DELAY_MS);
  }

  function checkTaskStatusAndReconnect() {
    if (closed) return;
    getTaskStatusSimple(taskId)
      .then(data => {
        if (closed) return;
        const status = data.status;
        if (TERMINAL_STATES.includes(status)) {
          if (callbacks.onComplete) callbacks.onComplete(status);
          cleanup();
        } else {
          // Task still running — REST succeeded, reset failure counter
          consecutiveFailures = 0;
          scheduleReconnect();
        }
      })
      .catch(() => {
        if (closed) return;
        consecutiveFailures++;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          console.error(`SSE reconnection giving up for task ${taskId} after ${MAX_CONSECUTIVE_FAILURES} consecutive failures`);
          if (callbacks.onError) callbacks.onError(new Error('SSE reconnection failed'));
          cleanup();
        } else {
          scheduleReconnect();
        }
      });
  }

  function connect() {
    if (closed) return;
    currentEventSource = new EventSource(`${process.env.VUE_APP_BASE_URL}/routes/task/info/${taskId}`);

    currentEventSource.onmessage = (event) => {
      if (closed) return;

      // TIMEOUT — expected hourly disconnect, reconnect immediately
      if (event.data === 'TIMEOUT') {
        console.log(`SSE timeout for task ${taskId}, reconnecting...`);
        currentEventSource.close();
        scheduleReconnect();
        return;
      }

      // Terminal state — task finished
      if (TERMINAL_STATES.includes(event.data)) {
        if (callbacks.onComplete) callbacks.onComplete(event.data);
        cleanup();
        return;
      }

      // Normal status update — reset failure counter
      consecutiveFailures = 0;
      if (callbacks.onMessage) callbacks.onMessage(event);
    };

    currentEventSource.onerror = () => {
      if (closed) return;
      currentEventSource.close();
      checkTaskStatusAndReconnect();
    };
  }

  connect();

  // Return wrapper with same .close() interface as EventSource
  return {
    close() {
      cleanup();
    }
  };
}

// Admin APIs
function updateUserAdmin(userId, userData) {
  return instance.put(`/routes/admin/users/${userId}`, userData);
}

function deleteUserAdmin(userId) {
  return instance.delete(`/routes/admin/users/${userId}`);
}

function deleteFileAdmin(fileId) {
  return instance.delete(`/routes/admin/files/${fileId}`);
}

function deleteWorkflowAdmin(workflowId) {
  return instance.delete(`/routes/admin/workflows/${workflowId}`);
}

export {
  registerUser,
  loginUser,
  getUser,
  getUsersCount,
  getFilesCount,
  getWorkflowsCount,
  getTasksCount,
  getPluginsCount,
  getFilteredUsers,
  getFilteredFiles,
  getFilteredWorkflows,
  getFilteredTasks,
  getFilteredPlugins,
  exportData,
  getResult,
  getResults,
  uploadForm,
  getFiles,
  getSharedFiles,
  findSharedFile,
  findFile,
  findFolder,
  getWorkflows,
  findWorkflow,
  saveWorkflow,
  deleteWorkflow,
  taskMonitoring,
  userTaskMonitoring,
  deleteFile,
  revokeTask,
  deleteTask,
  getTaskLogs,
  exportTaskLogsJSON,
  exportTaskLogTXT,
  downloadExecutionManifest,
  convertFile,
  getColumns,
  getClusters,
  setupAlgorithm,
  checkConvert,
  getOptions,
  checkOptions,
  getHtml,
  getDownloadResult,
  validationPlugin,
  uploadPluginMetadata,
  syncPluginData,
  uploadPluginScripts,
  uploadPluginPackage,
  uploadTextDependencies,
  getPlugins,
  associatePlugin,
  dissociatePlugin,
  getPluginTemplate,
  getDataTableFile,
  saveWorkflowNodeFile,
  deleteWorkflowNodeFile,
  readWorkflowNodeFile,
  getFileData,
  getSystemResources,
  getTaskResources,
  getPluginFile,
  getPluginPackageList,
  getPluginInfo,
  getVisualizationResult,
  runVisualization,
  createTaskEventSource,
  getPluginReferenceFolders,
  getTutorialFileDownload,
  updateUserAdmin,
  deleteUserAdmin,
  deleteFileAdmin,
  deleteWorkflowAdmin,
  buildPlugin,
  buildPluginDocker,
  checkPluginImage,
  getBuildStatus,
  getBuildTasks,
  cancelBuildTask,
  getBuildLogs,
  getPluginVersions,
  updatePluginVersion,
  // DAG Visualization APIs
  getDAGStructure,
  getRuleStatuses,
  getRuleLogs,
  getEnhancedProgress,
  // Cache Management APIs
  getDAGCacheStats,
  clearDAGCaches,
};
